import { JoinAsFundraiserComponent } from './../join-as-fundraiser/join-as-fundraiser.component';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DonationService } from 'src/app/shared/services/donation/donation.service';
import { UtilitiesService } from 'src/app/shared/services/utilities/utilities.service';
import { RichTextService } from 'src/app/shared/services/rich-text/rich-text.service';
import { LocalStorageService } from 'src/app/shared/services/local-storage/local-storage.service';
import { ApprovalStatusEnum } from 'src/app/shared/enum/approval-category.enum';
import { Meta } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import * as htmlToImage from 'html-to-image';

@Component({
  selector: 'app-campaign-details',
  templateUrl: './campaign-details.component.html',
  styleUrl: './campaign-details.component.scss',
})
export class CampaignDetailsComponent {
  @Input() showDonationSummary: boolean = true;
  @Input() showMoreDonorExperienceButton: boolean = true;
  @Input() showFundraisers: boolean = true;
  @Input() superAdminDashboard: boolean = false;

  @Input() ngoId: string | null = null;
  @Input() projectId: string | null = null;
  @Input() campaignId: string | null = null;

  public donationList: any;
  public currentDonationList: any;
  private donationListFetched: boolean = false; // Add this flag to donation data available or not
  public dayLeft: number | string;
  public donationListPopUp = false;
  public topDonations: any[];
  public recentDonations: any[];

  public ngo_id: string;
  public project_id: string;
  public campaign_id: string;

  public campaign: any;
  public approvalStatusEnum = ApprovalStatusEnum;

  public from: string = null;

  public isCommentHasData: boolean = false;
  public comment_reply: any[] = [];
  public activeFilter: string = 'recent';

  @ViewChild(JoinAsFundraiserComponent)
  joinAsFundraiserComponent: JoinAsFundraiserComponent;

  public currentEnvironment = environment;
  public shareToSocialMedia: boolean = false;
  @ViewChild('template', { static: false }) template!: ElementRef;
  public shareImage: any = null;

  constructor(
    private meta: Meta,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly donationService: DonationService,
    private readonly richTextService: RichTextService,
    public readonly utilitiesService: UtilitiesService,
    public readonly localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    this.updateMetaTags();
    if (this.ngoId && this.projectId && this.campaignId) {
      this.getCampaignDonationById(this.ngoId, this.projectId, this.campaignId);
      this.ngo_id = this.ngoId;
      this.project_id = this.projectId;
      this.campaign_id = this.campaignId;
    } else {
      this.route.queryParams.subscribe((params) => {
        const encryptedString = params['data'];
        const decryptedParams =
          this.localStorageService.decryptObjectUrl(encryptedString);
        if (decryptedParams) {
          this.campaign_id = decryptedParams.id;
          this.ngo_id = decryptedParams.ngo_id;
          this.project_id = decryptedParams.project_id;
          this.from = decryptedParams?.from;
          this.getCampaignDonationById(
            this.ngo_id,
            this.project_id,
            this.campaign_id
          );
        } else {
          this.router.navigateByUrl('/');
          console.error('Decryption failed');
        }
      });
    }
  }

  private updateMetaTags(): void {
    const metaTags = document.getElementsByTagName('meta');

    for (let i = 0; i < metaTags.length; i++) {
      if (metaTags[i].getAttribute('name') === 'description') {
        metaTags[i].setAttribute('property', 'og:description');
        metaTags[i].setAttribute(
          'content',
          'Dynamic meta description updated via JS'
        );
        return;
      }
    }

    // If not found, create a new one
    const newMeta = document.createElement('meta');
    newMeta.setAttribute('name', 'description');
    newMeta.setAttribute('property', 'og:description');
    newMeta.setAttribute('content', 'Dynamic meta description');
    document.head.appendChild(newMeta);
  }

  private getCampaignDonationById(
    ngo_id: string,
    project_id: string,
    campaign_id: string
  ) {
    this.donationService
      .getDonationCampaignById(ngo_id, project_id, campaign_id)
      .subscribe({
        next: (result) => {
          this.onSuccess(result);
        },
      });
  }

  private onSuccess(result) {
    this.campaign = result;
    this.meta.updateTag({
      property: 'og:title',
      content: this.campaign?.title,
    });

    this.campaign.cover_img_vdo = this.utilitiesService.jsonParse(
      this.campaign?.cover_img_vdo
    );

    this.campaign.story_rich_text = this.richTextService.decodeHtml(
      this.campaign?.story_rich_text
    );

    this.campaign.donation_summary = this.utilitiesService.jsonParse(
      this.campaign.donation_summary
    );

    this.campaign.ngo_logo = this.utilitiesService.jsonParse(result?.ngo_logo);

    this.getDaysLeft(this.campaign.end_date);
  }

  private getDaysLeft(endDate: string) {
    if (this.campaign.end_date) {
      const now = new Date().getDate();
      const end = new Date(endDate).getDate();

      if (end >= now) {
        this.dayLeft = Math.ceil((end - now) / (1000 * 3600 * 24));
      } else {
        this.dayLeft = 0;
      }
    } else {
      this.dayLeft = 'Ongoing';
    }
  }

  public getDonationTime(createdAt: string): string {
    const donationDate = new Date(createdAt);
    const currentDate = new Date();
    const timeDiffInMs = currentDate.getTime() - donationDate.getTime();
    const hours = Math.floor(timeDiffInMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''}`;
    } else {
      return `${hours + 1} hour${hours > 1 ? 's' : ''}`;
    }
  }

  public filterDonations(type: 'recent' | 'top') {
    this.activeFilter = type;
    if (type === 'recent') {
      this.currentDonationList = this.donationList.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    } else if (type === 'top') {
      this.currentDonationList = this.donationList.sort(
        (a, b) => b.amount - a.amount
      );
    }
  }

  public closePopup() {
    this.donationListPopUp = false;
  }

  public seeAll() {
    this.donationListPopUp = true;
    if (!this.donationListFetched) {
      this.donationService
        .getDonationListByCampaign(
          this.ngo_id,
          this.project_id,
          this.campaign_id
        )
        .subscribe({
          next: (result) => {
            this.donationListSuccess(result);
          },
        });
    } else {
      this.filterDonations('recent');
    }
  }

  private donationListSuccess(result: any) {
    this.donationList = result?.donations;
    this.donationListFetched = true;
    this.filterDonations('recent');
  }

  get getFundRaisedInPercent(): number {
    if (
      this.campaign?.total_fund_raised === null ||
      this.campaign?.total_fund_raised === 0
    ) {
      return 0;
    } else {
      return Math.round(
        (this.campaign?.total_fund_raised / this.campaign?.fundraising_target) *
          100
      );
    }
  }

  public navigateToCampaignDonation(campaign: any): void {
    const encryptedParams = this.localStorageService.encryptObjectUrl({
      id: campaign.id,
      ngo_id: campaign?.ngo_id,
      project_id: campaign?.project_id,
    });
    this.router.navigate(['/donate-to-campaign'], {
      queryParams: { data: encryptedParams },
    });
  }

  public async sharePost() {
    if (this.campaign?.short_url) {
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          environment.apiURL +
            '/shohay-ngo/api/v1/ngo-donation/campaign/social-media-share-by-short-url/' +
            this.campaign?.short_url
          //'TOZMkV4V'
        )}`
      );
    }
  }

  public async generateTemplate(): Promise<string> {
    const node = document.getElementById('template');

    if (!node) {
      throw new Error('Element not found');
    }

    try {
      const dataUrl = await htmlToImage.toPng(node);
      return dataUrl;
    } catch (err) {
      console.error('oops, something went wrong!', err);
      throw err;
    }
  }

  public getImage() {
    // this.donationService.getImage().subscribe((result: any) => {
    //   console.log(result);
    // })
    fetch(
      'https://hub.sensor.buzz/wash-files/api/v1/file-serve/45cac276-e5bc-4678-b3d1-deeffef6e229.jpg'
    )
      .then((response) => console.log('CORS is enabled!', response))
      .catch((error) => console.error('CORS issue:', error));
  }

  public copyUrl(campaign: any) {
    const shortUrl = `${window.location.origin}/share/${campaign?.short_url}`;
    navigator.clipboard.writeText(shortUrl).then(() => {
      this.utilitiesService.showSwalWithToast(
        `URL Copied`,
        `You copied the url`,
        'success'
      );
    });
  }
}
